import { Container, Label, VectorInput } from '@playcanvas/pcui';
import { Quat, TranslateGizmo, Vec3 } from 'playcanvas';

import { Pivot } from 'src/pivot';

import { TransformTool } from './transform-tool';
import { Events } from '../events';
import { Scene } from '../scene';

const v = new Vec3();

class MoveTool extends TransformTool {
    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const gizmo = new TranslateGizmo(scene.camera.entity.camera, scene.gizmoLayer);

        // ui
        const moveToolbar = new Container({
            id: 'move-toolbar',
            hidden: true
        });

        moveToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        const axis = new Container({
            class: 'transform-row'
        });


        const xLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'x'
        });

        const yLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'y'
        });

        const zLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'z'
        });

        axis.append(xLabel);
        axis.append(yLabel);
        axis.append(zLabel);

        const position = new Container({
            class: 'transform-row'
        });

        const positionVector = new VectorInput({
            class: 'transform-expand',
            precision: 3,
            dimensions: 3,
            value: [0, 0, 0],
            enabled: false
        });

        position.append(positionVector);

        moveToolbar.append(axis);
        moveToolbar.append(position);
        canvasContainer.append(moveToolbar);

        const toArray = (v: Vec3) => {
            return [v.x, v.y, v.z];
        };

        let uiUpdating = false;
        let mouseUpdating = false;

        // update UI with pivot
        const updateUI = (pivot: Pivot) => {
            uiUpdating = true;
            const transform = pivot.transform;
            positionVector.value = toArray(transform.position);
            uiUpdating = false;
        };

        // update pivot with UI
        const updatePivot = (pivot: Pivot) => {
            const transform = pivot.transform;
            const p = positionVector.value;
            const r = transform.rotation;
            const s = transform.scale;

            pivot.moveTRS(new Vec3(p[0], p[1], p[2]), r, s);
        };

        // handle a change in the UI state
        const change = () => {
            if (!uiUpdating) {
                const pivot = events.invoke('pivot') as Pivot;
                if (mouseUpdating) {
                    updatePivot(pivot);
                } else {
                    pivot.start();
                    updatePivot(pivot);
                    pivot.end();
                }
            }
        };

        const mousedown = () => {
            mouseUpdating = true;
            const pivot = events.invoke('pivot') as Pivot;
            pivot.start();
        };

        const mouseup = () => {
            const pivot = events.invoke('pivot') as Pivot;
            updatePivot(pivot);
            mouseUpdating = false;
            pivot.end();
        };

        [positionVector.inputs].flat().forEach((input) => {
            input.on('change', change);
            input.on('slider:mousedown', mousedown);
            input.on('slider:mouseup', mouseup);
        });

        // toggle ui availability based on selection
        events.on('selection.changed', (selection) => {
            positionVector.enabled = !!selection;
        });

        events.on('pivot.placed', (pivot: Pivot) => {
            updateUI(pivot);
        });

        events.on('pivot.moved', (pivot: Pivot) => {
            if (!mouseUpdating) {
                updateUI(pivot);
            }
        });

        events.on('pivot.ended', (pivot: Pivot) => {
            updateUI(pivot);
        });

        super(gizmo, events, scene, moveToolbar);
    }
}

export { MoveTool };
